import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, TextField, Select, MenuItem, Input, CardMedia } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import * as Yup from "yup";
import { UploadMedia } from "../../../utils/mediaUpload";
import { useLazyGetMoodByIdQuery, useUpdateMoodMutation } from "../../../services/moods";

const MOOD_TYPE = [
	{ value: "smile", label: "SMILE" },
	{ value: "ok", label: "OK" },
	{ value: "low", label: "LOW" },
	{ value: "angry", label: "ANGRY" },
	{ value: "sleep", label: "SLEEPY" },
	{ value: "cry", label: "CRY" },
];

const AddMood = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const idParam = searchParams.get("id");
	const moodId = idParam ? Number(idParam) : null;

	const [getMoodById] = useLazyGetMoodByIdQuery();

	const [emojiImageUrl, setEmojiImageUrl] = React.useState<string>("");
	const [emojiImageId, setEmojiImageId] = React.useState<number | null>(null);
	const [updateMood] = useUpdateMoodMutation();

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			moodType: "smile",
			reflectionPrompt: "",
		},
		validationSchema: Yup.object({
			moodType: Yup.string().oneOf(MOOD_TYPE.map((m) => m.value)).required("Mood Type is required"),
			reflectionPrompt: Yup.string().trim().required("Reflection Prompt is required"),
		}),
		onSubmit: async (values) => {
			const body: any = {
				mood_type: values.moodType,
				reflection_prompt: values.reflectionPrompt,
			};
			if (emojiImageId) {
				body.mood_image = emojiImageId;
			}

			try {
				if (moodId == null) {
					// console.log("Cannot update mood: missing moodId");
					return;
				}
				const response = await updateMood({ body, id: moodId as number }).unwrap();
				if (response?.status === 200) {
					navigate("/mood-mapping");
				}
			} catch (error) {
				console.log(error);
			}

		},
	});

	useEffect(() => {
		if (moodId) {
			(async () => {
				try {
					const resp = await getMoodById({ id: moodId }).unwrap();
					const data = (resp as any)?.data || {};
					const moodType = data?.mood_type || "smile";
					formik.setValues({
						moodType,
						reflectionPrompt: data?.reflection_prompt || "",
					});
					const mediaObj = data?.media || data?.mood_image;
					if (mediaObj?.media_file_url) {
						setEmojiImageUrl(mediaObj.media_file_url);
					}
					if (mediaObj?.id) {
						setEmojiImageId(mediaObj.id);
					}
				} catch (e) {
					// silently ignore prefill failures
				}
			})();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [moodId]);

	const handleEmojiUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		const file = files?.length ? files[0] : null;
		if (!file) {
			setEmojiImageUrl("");
			setEmojiImageId(null);
			return;
		}
		const response = (await UploadMedia(file)) as any;
		if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
			const uploaded = response.data[0];
			if (uploaded.media_file_url && uploaded.id) {
				setEmojiImageUrl(uploaded.media_file_url);
				setEmojiImageId(uploaded.id);
			}
		}
	};

	return (
		<>
			<div className="main_title">
				<h1>Edit Mood Mapping</h1>
				<p>
					<span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
					<span onClick={() => navigate("/mood-mapping")}>Emotional AI & Mood Mapping</span>{" "}- Add
				</p>
			</div>

			<div className="cards">
				<form className="form" onSubmit={formik.handleSubmit}>
					<div className="gap_p">
						<div className="control_group w_33" style={{ marginRight: 0 }}>
							<label>Upload Emoji</label>
							{emojiImageUrl ? (
								<div className="upload_image">
									<div className="upload_image_holder">
										<figure>
											<CardMedia component="img" image={emojiImageUrl} alt="emoji" />
										</figure>
										<CloseIcon
											onClick={() => {
												if (emojiImageId) {
													setEmojiImageUrl("");
													setEmojiImageId(null);
												} else {
													setEmojiImageUrl("");
												}
											}}
										/>
									</div>
								</div>
							) : (
								<label className="upload_image" htmlFor="emoji-button-file">
									<Input
										sx={{ display: "none" }}
										id="emoji-button-file"
										type="file"
										inputProps={{ accept: "image/png,image/jpeg" }}
										onChange={(e: any) => {
											handleEmojiUpload(e);
											e.target.value = "";
										}}
									/>
									<span className="upload_image_holder">
										<figure className="add_img">
											<img src="/static/images/user_placeholder.png" alt="icon" />
											<span className="upload_img_icon">
												<img src="/static/images/edit2_icon.svg" alt="icon" />
											</span>
										</figure>
									</span>
								</label>
							)}
						</div>
						<div className="control_group w_33 select_mood">
							<label>Mood Type</label>
							<Select
								name="moodType"
								value={formik.values.moodType}
								onChange={(e) => {
									const value = e.target.value as string;
									formik.setFieldValue("moodType", value);
								}}
								onBlur={formik.handleBlur}
								fullWidth
								displayEmpty
								disabled
							>
								{MOOD_TYPE.map((m) => (
									<MenuItem key={m.value} value={m.value}>
										{m.label}
									</MenuItem>
								))}
							</Select>
							{formik.touched.moodType && formik.errors.moodType ? (
								<div className="error_text">{formik.errors.moodType}</div>
							) : null}
						</div>

						<div className="control_group w_33">
							<label>Reflection Prompt</label>
							<TextField
								name="reflectionPrompt"
								placeholder="Enter Prompt"
								hiddenLabel
								fullWidth
								value={formik.values.reflectionPrompt}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								error={Boolean(formik.touched.reflectionPrompt && formik.errors.reflectionPrompt)}
								helperText={formik.touched.reflectionPrompt && formik.errors.reflectionPrompt}
							/>
						</div>


					</div>
					<div className="form_btn">
						<Button className="btn btn_primary" type="submit">Save</Button>
					</div>
				</form>
			</div>
		</>
	);
};

export default AddMood;
